// Central SEO utility. Sets <title>, <meta name="description">, Open Graph, Twitter,
// canonical link, and injects a JSON-LD <script type="application/ld+json"> per page.
import { useEffect } from "react";

// Site-wide constants. Update SITE_URL to the production origin once known.
export const SITE = {
  name: "Alae Dental",
  url: "https://www.alaedental.com", // canonical origin (update at go-live if different)
  defaultImage:
    "https://images.unsplash.com/photo-1567016376408-0226e4d0c1ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHx3YXJtJTIwbWluaW1hbGlzdCUyMGludGVyaW9yJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4Mjc0MjQ4Nnww&ixlib=rb-4.1.0&q=60&w=1600",
  defaultImageAlt:
    "Alae Dental, family and cosmetic dental practice in Vaughan, Ontario",
  locale: "en_CA",
  twitter: "@alaedental",
  // Bump when site content is materially updated. Surfaced as meta + a footer line.
  lastUpdatedISO: "2026-09-01",
  lastUpdatedLabel: "September 1, 2026",
};

function setMeta(attr, key, content) {
  if (content == null) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(url) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", url);
}

function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (el) el.remove();
  if (!data) return;
  el = document.createElement("script");
  el.type = "application/ld+json";
  el.id = id;
  el.text = JSON.stringify(data);
  document.head.appendChild(el);
}

/**
 * useSeo. Set head metadata + JSON-LD for the current page.
 * All fields optional except title + description.
 */
export function useSeo({
  title,
  description,
  path = "/",
  image,
  imageAlt,
  type = "website",
  jsonLd = null,
  jsonLdId = "page-jsonld",
}) {
  useEffect(() => {
    if (!title) return;
    const url = `${SITE.url}${path}`;
    const img = image || SITE.defaultImage;

    document.title = title;
    setMeta("name", "description", description);
    setMeta("name", "last-modified", SITE.lastUpdatedISO);
    setCanonical(url);

    setMeta("property", "og:site_name", SITE.name);
    setMeta("property", "og:type", type);
    setMeta("property", "og:locale", SITE.locale);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", img);
    setMeta("property", "og:image:alt", imageAlt || SITE.defaultImageAlt);

    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:site", SITE.twitter);
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", img);

    setJsonLd(jsonLdId, jsonLd);

    return () => {
      // leave meta in place across renders. SPA head persistence is fine
    };
  }, [title, description, path, image, imageAlt, type, jsonLd, jsonLdId]);
}

/* ---------- Reusable schema builders ---------- */

const ADDRESS = {
  "@type": "PostalAddress",
  streetAddress: "9097 Jane St",
  addressLocality: "Vaughan",
  addressRegion: "ON",
  postalCode: "L4K 0R7",
  addressCountry: "CA",
};

const HOURS = [
  {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Wednesday"],
    opens: "09:00",
    closes: "19:00",
  },
  {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Tuesday", "Thursday", "Friday"],
    opens: "09:00",
    closes: "18:00",
  },
  {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: "Saturday",
    opens: "09:00",
    closes: "15:00",
  },
];

export const dentalClinicSchema = () => ({
  "@context": "https://schema.org",
  "@type": ["Dentist", "LocalBusiness"],
  "@id": `${SITE.url}/#dentist`,
  name: SITE.name,
  description:
    "Family and cosmetic dental practice in Vaughan, Ontario. Preventive, restorative, cosmetic, and implant dentistry. All dental services and treatments at Alae Dental are provided by General Dentists.",
  image: `${SITE.url}/assets/alae-logo.png`,
  logo: `${SITE.url}/assets/alae-logo.png`,
  url: `${SITE.url}/`,
  telephone: "+1-365-360-2523",
  email: "info@alaedental.com",
  priceRange: "$$",
  currenciesAccepted: "CAD",
  address: ADDRESS,
  hasMap:
    "https://www.google.com/maps?q=9097+Jane+St,+Vaughan,+ON+L4K+0R7",
  areaServed: [
    { "@type": "City", name: "Vaughan" },
    { "@type": "City", name: "Woodbridge" },
    { "@type": "City", name: "Concord" },
    { "@type": "City", name: "Maple" },
    { "@type": "City", name: "Toronto" },
    { "@type": "AdministrativeArea", name: "Greater Toronto Area" },
  ],
  medicalSpecialty: "Dentistry",
  availableService: [
    "Preventive and Routine Dental Care",
    "Restorative Dentistry",
    "Cosmetic Dentistry",
    "Invisalign Clear Aligner Therapy",
    "Dental Implants",
    "Emergency Dentistry",
  ].map((name) => ({ "@type": "MedicalProcedure", name })),
  openingHoursSpecification: HOURS,
  founder: {
    "@type": "Person",
    name: "Dr. Nausheen Mirza",
    jobTitle: "General Dentist, Founder",
  },
  sameAs: [
    "https://share.google/vbMVUKWxdc1JV4puy",
    "https://www.instagram.com/alaedental/",
    "https://www.facebook.com/alaedental",
  ],
  // NOTE: aggregateRating and geo are intentionally omitted. The Google Business
  // Profile currently has a single review, and coordinates must come from GBP
  // rather than being estimated. Add both once confirmed.
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Dental Services",
    itemListElement: [
      "Preventive & Routine Care",
      "Restorative Dentistry",
      "Cosmetic Dentistry",
      "Invisalign",
      "Dental Implants",
      "Emergency Dentistry",
    ].map((s) => ({
      "@type": "Offer",
      itemOffered: { "@type": "Service", name: s },
    })),
  },
});

export const personSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": `${SITE.url}/about#person`,
  name: "Dr. Nausheen Mirza",
  givenName: "Nausheen",
  familyName: "Mirza",
  honorificPrefix: "Dr.",
  jobTitle: "General Dentist, Founder",
  description:
    "Dr. Nausheen Mirza is a General Dentist with over 13 years of clinical experience across Canada and the United States, practising family, restorative, cosmetic, and implant dentistry.",
  image: `${SITE.url}/assets/dr-mirza.jpg`,
  worksFor: { "@id": `${SITE.url}/#dentist` },
  alumniOf: [
    { "@type": "CollegeOrUniversity", name: "New York University College of Dentistry" },
    { "@type": "CollegeOrUniversity", name: "York University" },
    { "@type": "EducationalOrganization", name: "University of Florida (Implant Fellowship)" },
    { "@type": "EducationalOrganization", name: "Spear Education" },
    { "@type": "EducationalOrganization", name: "Las Vegas Institute for Advanced Dental Studies" },
  ],
  hasCredential: [
    {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "degree",
      name: "Doctor of Dental Surgery (DDS)",
      recognizedBy: {
        "@type": "CollegeOrUniversity",
        name: "New York University College of Dentistry",
      },
    },
    {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "degree",
      name: "Bachelor of Science with Honours, Biology",
      recognizedBy: { "@type": "CollegeOrUniversity", name: "York University" },
    },
    {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "fellowship",
      name: "Implant Fellowship",
      recognizedBy: {
        "@type": "CollegeOrUniversity",
        name: "University of Florida",
      },
    },
  ],
  affiliation: {
    "@type": "Organization",
    name: "University of Toronto Faculty of Dentistry",
    description: "Former clinical instructor",
  },
  knowsAbout: [
    "General Dentistry",
    "Cosmetic Dentistry",
    "Invisalign",
    "Dental Implants",
    "TMJ & Botox Therapy",
  ],
  url: `${SITE.url}/about`,
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const blogPostingSchema = (post) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": `${SITE.url}/blog/${post.slug}`,
  },
  headline: post.title,
  description: post.meta_description,
  image: SITE.defaultImage,
  author: {
    "@type": "Person",
    name: post.author || "Dr. Nausheen Mirza",
    url: `${SITE.url}/about`,
  },
  publisher: {
    "@type": "Organization",
    name: SITE.name,
    logo: {
      "@type": "ImageObject",
      url: "https://www.alaedental.com/assets/alae-logo.png",
    },
  },
  datePublished: post.published_at,
  dateModified: post.published_at,
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE.url}${it.path}`,
  })),
});
